/**
 * ThemeProvider — troca de tema em runtime para toda a SPA.
 *
 * Mecanismo: `applyTheme` muta os valores de `C` (mesma referência) e este
 * provider força um REMOUNT da subárvore via `key={theme}`, fazendo todos os
 * estilos inline (`style={{ color: C.x }}`) e o NavaStyles relerem `C`.
 * A escolha é persistida em localStorage e reaplicada no próximo carregamento
 * (o palette.js já aplica o tema persistido no load do módulo).
 *
 * Fica ACIMA do AppRouter e ABAIXO de Auth/Api — o remount não afeta a sessão
 * (token em memória) nem o cache do React Query.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { applyTheme, resolveInitialTheme, THEME_STORAGE_KEY, THEMES } from './palette';

const ThemeContext = createContext(null);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme deve ser usado dentro de <ThemeProvider>');
  return ctx;
}

export function ThemeProvider({ children }) {
  // palette.js já aplicou o tema persistido no load; só espelhamos o id.
  const [theme, setThemeState] = useState(() => resolveInitialTheme());

  const setTheme = useCallback((name) => {
    if (!THEMES[name]) return;
    applyTheme(name);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, name);
    } catch {
      /* localStorage indisponível — mantém só em memória nesta sessão */
    }
    setThemeState(name);
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {/* key={theme} remonta a subárvore para reler os valores mutados de C.
          display:contents evita interferência no layout. */}
      <div style={{ display: 'contents' }} key={theme}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}
